import React, { useState } from 'react';
import { itineraries as romaniaItineraries, BOOKING_LINK, Itinerary } from '@/data/romaniaHub';
import { useNavigate } from 'react-router-dom';
import { Calendar, MapPin, ArrowRight, ChevronRight } from 'lucide-react';

interface Props {
  onInquire: () => void;
  /** Optional itinerary set — defaults to the Romania itineraries. */
  items?: Itinerary[];
  /** Optional booking route — defaults to the Romania booking link. */
  bookingLink?: string;
}

const CuratedItineraries: React.FC<Props> = ({
  onInquire,
  items = romaniaItineraries,
  bookingLink = BOOKING_LINK,
}) => {
  const navigate = useNavigate();
  const [active, setActive] = useState(items[0].id);
  const current = items.find((i) => i.id === active) ?? items[0];

  return (
    <section id="itineraries" className="py-24 lg:py-32 bg-[#FAF8F3]">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-12 h-[1px] bg-[#C9A961]" />
            <span className="font-sans text-[10px] tracking-[0.4em] uppercase text-[#C9A961]">
              Sample Journeys
            </span>
            <div className="w-12 h-[1px] bg-[#C9A961]" />
          </div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#1A3B5C] font-light mb-6">
            Curated Itineraries
          </h2>
          <p className="font-sans text-sm text-[#666] max-w-2xl mx-auto leading-relaxed">
            Beautifully paced sample journeys — each fully customisable. Select an itinerary to
            explore its daily timeline.
          </p>
        </div>

        {/* Selector tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {items.map((it) => (
            <button
              key={it.id}
              onClick={() => setActive(it.id)}
              className={`font-sans text-[11px] tracking-[0.15em] uppercase px-5 py-3 border transition-all duration-300 ${
                active === it.id
                  ? 'bg-[#1A3B5C] text-white border-[#1A3B5C]'
                  : 'bg-white text-[#1A3B5C] border-[#E8DFD0] hover:border-[#C9A961]'
              }`}
            >
              {it.duration}
            </button>
          ))}
          <button
            onClick={onInquire}
            className="font-sans text-[11px] tracking-[0.15em] uppercase px-5 py-3 border bg-[#C9A961] text-white border-[#C9A961] hover:bg-[#b8944d] hover:border-[#b8944d] transition-all duration-300"
          >
            À La Carte Experience
          </button>
        </div>


        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          {/* Image + meta */}
          <div className="lg:sticky lg:top-28">
            <div className="relative aspect-[4/3] overflow-hidden mb-6">
              <img src={current.image} alt={current.imageAlt} loading="lazy" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              <div className="absolute bottom-6 left-6 text-white">
                <span className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#C9A961]">{current.duration}</span>
                <h3 className="font-serif text-2xl md:text-3xl font-light mt-1">{current.title}</h3>
              </div>
            </div>
            <p className="font-sans text-sm text-[#555] leading-relaxed mb-5">{current.summary}</p>
            <div className="flex flex-wrap gap-2 mb-6">
              {current.regions.map((r) => (
                <span key={r} className="flex items-center gap-1 font-sans text-[10px] tracking-wide uppercase text-[#8a7a55] bg-[#FAF3E4] px-3 py-1.5">
                  <MapPin className="w-3 h-3" /> {r}
                </span>
              ))}
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => navigate(bookingLink)}
                className="font-sans text-[11px] tracking-[0.25em] uppercase px-8 py-3.5 bg-[#C9A961] text-white hover:bg-[#b8944d] transition-colors flex items-center gap-3"
              >
                Book This Journey <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={onInquire}
                className="font-sans text-[11px] tracking-[0.25em] uppercase px-8 py-3.5 border border-[#1A3B5C] text-[#1A3B5C] hover:bg-[#1A3B5C] hover:text-white transition-colors"
              >
                Customise
              </button>
            </div>
          </div>

          {/* Timeline */}
          <div>
            <p className="flex items-center gap-2 font-sans text-[10px] tracking-[0.3em] uppercase text-[#C9A961] mb-8">
              <Calendar className="w-4 h-4" /> Daily Timeline
            </p>
            <ol className="relative border-l border-[#E8DFD0] ml-2 space-y-8">
              {current.days.map((d) => (
                <li key={d.day} className="pl-8 relative">
                  <span className="absolute -left-[7px] top-1 w-3 h-3 rounded-full bg-[#C9A961] border-2 border-[#FAF8F3]" />
                  <p className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#C9A961] mb-1">{d.day}</p>
                  <h4 className="font-serif text-xl text-[#1A3B5C] font-light mb-2 flex items-center gap-2">
                    {d.title}
                  </h4>
                  <p className="font-sans text-xs text-[#666] leading-relaxed">{d.detail}</p>
                </li>
              ))}
            </ol>

            <div className="mt-10 p-6 bg-white border border-[#E8DFD0]">
              <p className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#C9A961] mb-3">Included in Every Journey</p>
              <ul className="space-y-2">
                {['Private guide & chauffeur', 'Hand-picked luxury accommodation', 'Curated activities & access', 'Seamless transfers throughout', 'Optional luxury upgrades'].map((x) => (
                  <li key={x} className="flex items-center gap-2 font-sans text-xs text-[#555]">
                    <ChevronRight className="w-3.5 h-3.5 text-[#C9A961]" /> {x}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CuratedItineraries;
